import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/*
    🔥 VIRTUALIZACIÓN DE LISTAS

    ⚠️ EL PROBLEMA QUE RESUELVE

    Tu DataTable pinta un <tr> por fila. Con 5.000 filas eso son 5.000
    nodos por cada re-render, y el navegador se arrastra. Virtualizar
    significa pintar solo las ~25 que se ven y simular el resto con
    altura vacía.

    ⚠️ ES OPT-IN, NO REEMPLAZA A LA TABLA

    Con 200 filas la virtualización SUMA complejidad y no gana nada:
    hay que medir, hay que fijar altura de fila, y se pierde el
    Ctrl+F del navegador porque el texto no está en el DOM. Por eso la
    tabla actual queda intacta y esto se usa cuando de verdad hace
    falta.

    ⚠️ LA ALTURA DE FILA TIENE QUE SER FIJA

    Es la condición para que la matemática funcione sin medir cada
    elemento. Filas de alto variable exigen medición y caché, que es
    otro problema entero. Si tu fila crece con el contenido, ponele
    altura fija por CSS antes de virtualizar.

    ⚠️ overscan NO ES UN CAPRICHO

    Sin filas de más arriba y abajo del área visible, al scrollear
    rápido se ve el blanco antes de que React pinte. Cinco alcanzan.
*/

export const useVirtualList = ({
    count = 0,
    rowHeight = 44,
    overscan = 5,
    getScrollElement = null,     // por defecto, el contenedor propio
} = {}) => {

    const REF__useRefScroll = useRef(null);

    const [useScrollTop, setScrollTop] = useState(0);
    const [useHeight, setHeight] = useState(0);

    const TOTAL_HEIGHT = count * rowHeight;

    const MEMO__RANGE = useMemo(() => {

        if (!useHeight || !count) return { start: 0, end: Math.min(count, overscan * 2) };

        const FIRST = Math.floor(useScrollTop / rowHeight);

        const VISIBLE = Math.ceil(useHeight / rowHeight);

        return {
            start: Math.max(0, FIRST - overscan),
            end: Math.min(count, FIRST + VISIBLE + overscan),
        };

    }, [useScrollTop, useHeight, count, rowHeight, overscan]);

    const MEMO__ITEMS = useMemo(() => (
        Array.from({ length: Math.max(0, MEMO__RANGE.end - MEMO__RANGE.start) }, (_, i) => {
            const INDEX = MEMO__RANGE.start + i;
            return { index: INDEX, offset: INDEX * rowHeight };
        })
    ), [MEMO__RANGE, rowHeight]);

    // -------------------- 🎯 MEDICIÓN
    useEffect(() => {

        const EL = getScrollElement?.() ?? REF__useRefScroll.current;

        if (!EL) return;

        const onMeasure = () => setHeight(EL.clientHeight);

        onMeasure();

        /*
            🔥 ResizeObserver y no window.resize: el contenedor puede
               cambiar de alto sin que cambie la ventana —al abrir un
               panel lateral, por ejemplo—.
        */
        const RO = new ResizeObserver(onMeasure);
        RO.observe(EL);

        return () => RO.disconnect();

    }, [getScrollElement]);

    // -------------------- 🎯 SCROLL
    const CB__onScroll = useCallback((event) => {
        setScrollTop(event.currentTarget.scrollTop);
    }, []);

    const CB__onScrollToIndex = useCallback((index, align = 'start') => {

        const EL = getScrollElement?.() ?? REF__useRefScroll.current;

        if (!EL) return;

        const TOP = index * rowHeight;

        EL.scrollTop = align === 'center'
            ? TOP - EL.clientHeight / 2 + rowHeight / 2
            : TOP;

    }, [rowHeight, getScrollElement]);

    return {
        scrollRef: REF__useRefScroll,
        items: MEMO__ITEMS,
        totalHeight: TOTAL_HEIGHT,
        rowHeight,
        range: MEMO__RANGE,
        onScroll: CB__onScroll,
        onScrollToIndex: CB__onScrollToIndex,
    };
};
